// src/components/CustomerDocuments.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Download,
  Folder,
  Description,
  Receipt,
  Image,
  PictureAsPdf,
} from "@mui/icons-material";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";

function CustomerDocuments() {
  const { customer } = useCustomerAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch documents from completed jobs
  useEffect(() => {
    if (!customer) return;

    const jobsQuery = query(
      collection(db, "jobs"),
      where("customerName", "==", customer.name),
      where("status", "==", "Completed")
    );

    const unsubscribe = onSnapshot(jobsQuery, (snapshot) => {
      const docsList = [];
      
      snapshot.docs.forEach(doc => {
        const job = doc.data();
        
        // Add completion certificate
        docsList.push({
          id: `cert-${doc.id}`,
          type: "certificate",
          name: `Completion Certificate - ${job.jobTemplateName}`,
          jobName: job.jobTemplateName,
          jobRef: job.jobReferenceNumber,
          date: job.completedDate || job.createdAt,
          icon: <Description />,
        });

        // Add photos if available
        if (job.completionPhotos && job.completionPhotos.length > 0) {
          job.completionPhotos.forEach((photo, index) => {
            docsList.push({
              id: `photo-${doc.id}-${index}`,
              type: "photo",
              name: `Job Photo ${index + 1} - ${job.jobTemplateName}`,
              jobName: job.jobTemplateName,
              jobRef: job.jobReferenceNumber,
              date: job.completedDate || job.createdAt,
              url: photo,
              icon: <Image />,
            });
          });
        }
      });

      // Add invoices
      const invoicesQuery = query(
        collection(db, "invoices"),
        where("customerName", "==", customer.name)
      );

      const invoicesUnsub = onSnapshot(invoicesQuery, (invSnapshot) => {
        invSnapshot.docs.forEach(doc => {
          const invoice = doc.data();
          docsList.push({
            id: `invoice-${doc.id}`,
            type: "invoice",
            name: invoice.invoiceNumber || `Invoice #${doc.id.substring(0, 8)}`,
            jobName: invoice.jobTemplateName,
            date: invoice.createdAt,
            icon: <Receipt />,
          });
        });

        setDocuments(docsList.sort((a, b) => {
          const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
          const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
          return dateB - dateA;
        }));
        setLoading(false);
      });

      return () => invoicesUnsub();
    });

    return () => unsubscribe();
  }, [customer]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const getDocTypeColor = (type) => {
    switch (type) {
      case "certificate":
        return "success";
      case "invoice":
        return "error";
      case "photo":
        return "info";
      case "report":
        return "warning";
      default:
        return "default";
    }
  };

  const handleDownload = (doc) => {
    if (doc.url) {
      window.open(doc.url, "_blank");
    } else {
      alert(`Downloading ${doc.name}...\n\nPDF generation coming soon!`);
    }
  };

  const handleDownloadAll = () => {
    alert(`Downloading all documents as ZIP...\n\nFeature coming soon!`);
  };

  // Group documents by type
  const groupedDocs = documents.reduce((acc, doc) => {
    if (!acc[doc.type]) acc[doc.type] = [];
    acc[doc.type].push(doc);
    return acc;
  }, {});

  const stats = {
    total: documents.length,
    certificates: groupedDocs.certificate?.length || 0,
    invoices: groupedDocs.invoice?.length || 0,
    photos: groupedDocs.photo?.length || 0,
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h4" sx={{ color: "white", fontWeight: 700, mb: 1 }}>
            Documents
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Access all your service documents and certificates
          </Typography>
        </Box>
        {documents.length > 0 && (
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadAll}
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #dc2626 100%)",
              },
            }}
          >
            Download All
          </Button>
        )}
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", color: "white" }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>{stats.total}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Documents</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "white" }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>{stats.certificates}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Certificates</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", color: "white" }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>{stats.invoices}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Invoices</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)", color: "white" }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>{stats.photos}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Photos</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Documents List */}
      {documents.length === 0 ? (
        <Alert severity="info">
          No documents available yet. Documents will appear here once jobs are completed.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {documents.map((doc) => (
            <Paper
              key={doc.id}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#f59e0b",
                  boxShadow: "0 4px 12px rgba(245,158,11,0.2)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    background: `linear-gradient(135deg, ${
                      doc.type === "certificate" ? "#10b981" :
                      doc.type === "invoice" ? "#ef4444" :
                      doc.type === "photo" ? "#3b82f6" :
                      "#8b5cf6"
                    } 0%, rgba(255,255,255,0.1) 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  {doc.icon}
                </Box>

                {/* Details */}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {doc.name}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(doc.date)}
                    </Typography>
                    {doc.jobRef && (
                      <Typography variant="caption" color="text.secondary">
                        • Ref: {doc.jobRef}
                      </Typography>
                    )}
                  </Stack>
                </Box>

                {/* Type Badge */}
                <Chip
                  label={doc.type.toUpperCase()}
                  color={getDocTypeColor(doc.type)}
                  size="small"
                />

                {/* Download Button */}
                <Tooltip title="Download">
                  <IconButton
                    onClick={() => handleDownload(doc)}
                    color="primary"
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Info Card */}
      <Card sx={{ mt: 3, bgcolor: "rgba(59,130,246,0.05)" }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            📄 Document Types
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            • <strong>Certificates:</strong> Completion certificates for finished jobs<br />
            • <strong>Invoices:</strong> All your billing documents<br />
            • <strong>Photos:</strong> Before/after photos of completed work<br />
            • <strong>Reports:</strong> Detailed service reports and findings
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default CustomerDocuments;
